/**
 * Event -> Budget & Expenses tab (finance expenses increment 1b/1c).
 *
 * Rendered only when the financeExpenses flag is on AND finance_my_access(event) says the module is enabled and
 * the caller has finance access to this event (EventDetail decides). Budget metrics are informational: nothing
 * here blocks or alerts on overspend (requirements section 5.5).
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BanknotesIcon } from "@heroicons/react/24/outline";
import Button from "../../common/Button";
import ExpenseTable from "../../finance/ExpenseTable";
import ExpenseFormModal from "../../finance/ExpenseFormModal";
import ExpenseDetailModal from "../../finance/ExpenseDetailModal";
import {
  financeErrorMessage,
  getBudgetSummary,
  getFinanceLookups,
  listEventExpenses,
  money,
  setEventBudget,
} from "../../../services/finance.service";
import type { FinanceAccess } from "../../../types/finance.types";

interface Props {
  eventId: string;
  access: FinanceAccess;
}

function Tile({
  label,
  value,
  hint,
  testId,
}: {
  label: string;
  value: string;
  hint?: string;
  testId?: string;
}) {
  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-4"
      data-testid={testId}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-gray-900">
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export default function BudgetExpensesTab({ eventId, access }: Props) {
  const qc = useQueryClient();
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");
  const [budgetError, setBudgetError] = useState<string | null>(null);
  const [savingBudget, setSavingBudget] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const summaryQ = useQuery({
    queryKey: ["finance-budget", eventId],
    queryFn: () => getBudgetSummary(eventId),
  });
  const expensesQ = useQuery({
    queryKey: ["finance-expenses", "event", eventId],
    queryFn: () => listEventExpenses(eventId),
  });
  const lookupsQ = useQuery({
    queryKey: ["finance-lookups"],
    queryFn: getFinanceLookups,
    staleTime: 60_000,
  });

  const s = summaryQ.data;
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["finance-budget", eventId] });
    qc.invalidateQueries({ queryKey: ["finance-expenses"] });
  };

  const saveBudget = async () => {
    const n = Number(budgetInput);
    if (budgetInput.trim() === "" || !Number.isFinite(n) || n < 0) {
      setBudgetError("Enter a budget of 0 or more.");
      return;
    }
    setSavingBudget(true);
    setBudgetError(null);
    try {
      await setEventBudget(eventId, Math.round(n * 100) / 100);
      setEditingBudget(false);
      refresh();
    } catch (e) {
      setBudgetError(financeErrorMessage(e));
    } finally {
      setSavingBudget(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="finance-budget-tab">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <BanknotesIcon className="h-5 w-5 text-gray-500" /> Budget &amp;
            expenses
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Plan anticipated costs for this event and send them for approval.
            Budget figures are for information only; they do not block anything.
          </p>
        </div>
        {!access.event_cancelled && (
          <Button
            onClick={() => setAddOpen(true)}
            data-testid="finance-add-expense"
          >
            Add expense
          </Button>
        )}
      </div>

      {access.event_cancelled && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
          data-testid="finance-event-cancelled"
        >
          This event is cancelled, so no new expenses can be added or submitted.
          Existing expenses stay here; cancel any that will not happen.
        </div>
      )}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        {editingBudget ? (
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label
                htmlFor="fx-budget"
                className="block text-sm font-medium text-gray-700"
              >
                Total budget (USD)
              </label>
              <input
                id="fx-budget"
                type="number"
                min="0"
                step="0.01"
                className="mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
              />
            </div>
            <Button size="sm" onClick={saveBudget} disabled={savingBudget}>
              {savingBudget ? "Saving…" : "Save budget"}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setEditingBudget(false)}
              disabled={savingBudget}
            >
              Cancel
            </Button>
            {budgetError && (
              <p role="alert" className="w-full text-sm text-red-700">
                {budgetError}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-700">Total budget:</span>
            <span
              className="text-lg font-semibold tabular-nums"
              data-testid="finance-total-budget"
            >
              {s?.total_budget !== null && s?.total_budget !== undefined
                ? money(s.total_budget)
                : "Not set"}
            </span>
            {/* 1141: only event staff / finance change the budget; admins with view-only event access do not */}
            {access.event_work_access === true && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setBudgetInput(
                    s?.total_budget !== null && s?.total_budget !== undefined
                      ? String(s.total_budget)
                      : "",
                  );
                  setBudgetError(null);
                  setEditingBudget(true);
                }}
              >
                {s?.total_budget !== null && s?.total_budget !== undefined
                  ? "Change"
                  : "Set budget"}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Tile
          label="Approved commitments"
          value={money(s?.committed_total ?? 0)}
          hint="Approved, awaiting invoice"
          testId="finance-tile-committed"
        />
        <Tile
          label="Remaining uncommitted"
          value={
            s?.remaining_uncommitted !== null &&
            s?.remaining_uncommitted !== undefined
              ? money(s.remaining_uncommitted)
              : "—"
          }
          hint={
            s?.total_budget === null || s?.total_budget === undefined
              ? "Set a budget to see this"
              : "Budget minus commitments and actuals"
          }
          testId="finance-tile-remaining"
        />
        <Tile
          label="Actuals"
          value={money(s?.actual_total ?? 0)}
          hint="Invoices recorded (any stage)"
          testId="finance-tile-actual"
        />
        <Tile
          label="Pending approval"
          value={money(s?.pending_total ?? 0)}
          hint="Not yet a commitment"
        />
        <Tile
          label="Drafts"
          value={money(s?.draft_total ?? 0)}
          hint="Draft or returned"
        />
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        {expensesQ.isLoading ? (
          <p className="py-8 text-center text-sm text-gray-500">
            Loading expenses…
          </p>
        ) : expensesQ.error ? (
          <p className="py-8 text-center text-sm text-red-700">
            {financeErrorMessage(expensesQ.error)}
          </p>
        ) : (
          <ExpenseTable
            expenses={expensesQ.data ?? []}
            lookups={lookupsQ.data}
            onOpen={setOpenId}
            empty={
              access.event_cancelled
                ? "No expenses were planned for this event."
                : "No expenses yet. Use Add expense to plan the first one."
            }
          />
        )}
      </div>

      <ExpenseFormModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={(e) => {
          setAddOpen(false);
          refresh();
          setOpenId(e.id);
        }}
        eventId={eventId}
        isFinanceAdmin={!!access.is_finance_admin}
      />
      <ExpenseDetailModal
        expenseId={openId}
        onClose={() => setOpenId(null)}
        onChanged={refresh}
      />
    </div>
  );
}
